<template>
    <div class="flex h-[calc(100vh-64px)] bg-gray-100 overflow-hidden">
        <!-- 메인 컨텐츠 영역 (85% 너비) -->
        <div class="flex-[0.85] flex flex-col p-4 overflow-hidden min-h-0">
            <!-- 헤더 -->
            <div class="flex justify-between items-start mb-4 pb-3 border-b-2 border-gray-200 flex-shrink-0">
                <h1 class="text-3xl font-bold text-gray-800 m-0"></h1>
                <div class="flex flex-col gap-4 items-end">
                    <!-- 날짜 선택기 -->
                    <div class="flex items-center gap-2 bg-white px-3 py-2 rounded-md shadow-sm border border-gray-300">
                        <label class="text-sm font-medium text-gray-600 whitespace-nowrap">조회 날짜:</label>
                        <input type="date" v-model="selectedDate" @change="onDateChange"
                            class="border-0 outline-0 text-sm text-gray-800 bg-transparent font-mono cursor-pointer" />
                    </div>
                    <!-- 동기화 정보 -->
                    <div class="flex flex-col gap-2 items-end">
                        <div class="flex items-center gap-2 text-xs">
                            <span class="text-gray-600 font-medium">채팅 업데이트:</span>
                            <span class="text-gray-800 bg-gray-200 px-2 py-1 rounded text-xs font-mono">{{
                                formatSyncTime(syncRecord?.chatSynchronizedAt) }}</span>
                        </div>
                        <div class="flex items-center gap-2 text-xs">
                            <span class="text-gray-600 font-medium">거래내역 업데이트:</span>
                            <span class="text-gray-800 bg-gray-200 px-2 py-1 rounded text-xs font-mono">{{
                                formatSyncTime(syncRecord?.creditTxSynchronizedAt) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 테이블 영역 -->
            <div class="bg-white rounded-xl shadow-md overflow-hidden flex-1 flex flex-col min-h-0 h-0 relative">
                <!-- 로딩 오버레이 -->
                <div v-if="loading"
                    class="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-20">
                    <div class="flex flex-col items-center gap-3">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
                        <div class="text-sm text-gray-600 font-medium">데이터를 불러오는 중...</div>
                    </div>
                </div>

                <div class="flex-1 overflow-auto min-h-0" @mouseleave="onTableMouseLeave" @mouseup="onTableMouseUp">
                    <table class="w-full border-collapse text-sm select-none" @selectstart="preventSelection">
                        <thead>
                            <tr>
                                <th
                                    class="bg-gray-700 text-white p-4 text-center font-semibold border-r border-gray-600 min-w-[150px] text-left sticky top-0 z-10">
                                    채권그룹</th>
                                <th v-for="maturity in maturityColumns" :key="maturity"
                                    class="bg-gray-700 text-white p-3 text-center font-semibold border-r border-gray-600 last:border-r-0 min-w-[80px] sticky top-0 z-10">
                                    {{ maturity }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(group, index) in bondGroups" :key="group.id">
                                <!-- 섹터 구분선 (새로운 섹터의 첫 번째 그룹일 때) -->
                                <tr v-if="isFirstGroupInSector(group, index)" class="bg-gray-100">
                                    <td
                                        class="p-2 text-center text-sm font-bold text-gray-600 border-b-2 border-gray-300 border-r border-gray-200">
                                        {{ group.sectorName }}
                                    </td>
                                    <td v-for="maturity in maturityColumns" :key="`sector-${group.sector}-${maturity}`"
                                        class="p-2 border-b-2 border-gray-300 border-r border-gray-200 last:border-r-0 bg-gray-100">
                                    </td>
                                </tr>

                                <!-- 일반 그룹 행 -->
                                <tr class="hover:bg-gray-50" :class="{ 'bg-gray-50/50': group.isOtherGroup }">
                                    <td class="p-3 border-b border-gray-200 border-r border-gray-200 font-semibold text-gray-800"
                                        :class="{ 'text-gray-600': group.isOtherGroup }">
                                        {{ group.name }}
                                    </td>
                                    <td v-for="maturity in maturityColumns" :key="maturity"
                                        class="p-3 border-b border-gray-200 border-r border-gray-200 last:border-r-0 text-center cursor-pointer relative transition-all duration-200 select-none"
                                        :class="{
                                            'bg-green-100': hasQuoteInCell(group.id, maturity),
                                            'hover:bg-green-200': hasQuoteInCell(group.id, maturity),
                                            'hover:bg-blue-50': !hasQuoteInCell(group.id, maturity),
                                            'bg-blue-200 !border-2 !border-blue-500': isSelectedCell(group.id, maturity),
                                            'bg-blue-100 !border-2 !border-dashed !border-blue-400 opacity-80': isDragSelecting(group.id, maturity)
                                        }" @mousedown="onCellMouseDown(group, maturity, $event)"
                                        @mouseenter="onCellMouseEnter(group, maturity)" @mouseup="onCellMouseUp">
                                        <!-- 호가 존재 여부만 표시 -->
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- 우측 사이드바 (15% 너비) -->
        <div
            class="flex-[0.15] bg-gradient-to-b from-gray-700 via-gray-800 to-gray-900 flex flex-col overflow-hidden relative">
            <div class="absolute inset-0 bg-gradient-to-br from-gray-700/95 to-gray-800/90 backdrop-blur-sm"></div>

            <!-- 문의 영역 -->
            <div class="flex-1 p-5 flex flex-col text-white relative z-10">
                <div class="mb-4">
                    <h3 class="text-lg font-bold text-white m-0">문의하기</h3>
                </div>
                <div class="flex-1 flex flex-col gap-4">
                    <div class="flex-1 flex flex-col">
                        <div class="text-sm font-semibold text-white/90 mb-2">선택된 셀:</div>
                        <div class="flex-1 overflow-y-auto bg-white/10 rounded-lg p-2">
                            <div v-if="selectedCells.length === 0" class="text-xs text-white/70 text-center py-8">
                                테이블에서 셀을 클릭하여 선택하세요
                            </div>
                            <div v-for="cell in selectedCells" :key="cell.key"
                                class="flex justify-between items-center bg-white/20 p-2 rounded-md mb-1 text-xs">
                                {{ cell.groupName }} - {{ cell.maturity }}
                                <button @click="removeSelectedCell(cell.key)"
                                    class="bg-white/30 hover:bg-white/50 border-0 rounded-full w-5 h-5 text-white cursor-pointer flex items-center justify-center text-sm leading-none transition-all">×</button>
                            </div>
                        </div>
                    </div>
                    <div class="flex-shrink-0 flex flex-col">
                        <textarea v-model="inquiryMessage" placeholder="문의 내용 입력 후 복사하여 텔레그램 전송"
                            class="h-60 p-3 border-0 rounded-lg bg-white/90 text-gray-800 text-sm resize-none outline-0 placeholder-gray-600"
                            rows="3"></textarea>
                        <button @click="copyToClipboard"
                            class="mt-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
                            <i class="fas fa-copy"></i>
                            클립보드에 복사
                        </button>
                    </div>
                </div>
            </div>

            <!-- 담당자 목록 -->
            <div class="flex-shrink-0 p-5 border-t border-white/20 relative z-10">
                <div class="mb-4 text-center">
                    <h3 class="text-base font-bold text-white m-0">담당자</h3>
                </div>
                <div class="flex flex-col gap-3">
                    <a v-for="contact in contacts" :key="contact.name" :href="getTelegramLink(contact.phone)"
                        target="_blank"
                        class="flex justify-between items-center bg-white/10 p-3 rounded-lg backdrop-blur-sm border border-white/20 transition-all hover:bg-white/20 hover:-translate-y-0.5 hover:shadow-lg no-underline cursor-pointer"
                        :title="`${contact.name}에게 텔레그램으로 연락하기`">
                        <div class="flex-1">
                            <div class="text-sm font-semibold text-white">{{ contact.name }}</div>
                        </div>
                        <div
                            class="flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full text-white text-sm shadow-md">
                            <i class="fab fa-telegram-plane text-white"></i>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import bondService from '@/service/bondService';

export default {
    name: 'YuantaView',
    data() {
        return {
            bondGroups: [],
            quotes: [],
            syncRecord: null,
            loading: false,
            selectedDate: new Date().toISOString().split('T')[0], // 오늘 날짜로 초기화
            maturityColumns: ['3M', '6M', '9M', '1Y', '1.5Y', '2Y', '2.5Y', '3Y', '4Y', '5Y', '6Y', '7Y', '10Y'],
            contacts: [
                { name: '김상균', phone: '010-9358-1254' },
                { name: '노규범', phone: '010-9594-2635' },
                { name: '조만규', phone: '010-9418-9871' },
                { name: '김선민', phone: '010-3035-9846' },
                { name: '왕건호', phone: '010-2340-0743' },
                { name: '권혁천', phone: '010-5711-0179' }
            ],
            creditTypes: ['PUBLIC', 'BANK', 'CREDIT_FINANCE', 'COMPANY'],
            // 셀별 호가 매핑 데이터 (확장 가능한 구조)
            cellQuoteMap: new Map(), // key: `${groupId}-${maturity}`, value: quotes array
            // 선택된 셀들
            selectedCells: [], // { key, groupId, groupName, maturity }
            inquiryMessage: '',
            // 드래그 선택 관련
            isDragging: false,
            dragStartCell: null,
            dragMode: null, // 'select' 또는 'deselect'
            dragSelection: new Set(), // 드래그 중 임시 선택된 셀들
            mouseDownTime: 0,
            mouseDownPosition: null,
            // 폴링 관련 상태
            pollingInterval: null
        };
    },
    async mounted() {
        console.log('YuantaView 마운트 시작');
        this.loading = true;

        try {
            // 1단계: 채권 그룹 먼저 로드
            await this.loadBondGroups();
            console.log('채권 그룹 로드 완료, 호가 데이터 로드 시작');

            // 2단계: 채권 그룹 준비 후 호가와 동기화 기록 로드
            await Promise.all([
                this.loadSyncRecord(),
                this.loadQuotes()
            ]);
            console.log('초기 데이터 로드 완료');
        } catch (error) {
            console.error('초기 데이터 로드 실패:', error);
        } finally {
            this.loading = false;
        }

        // 30초마다 호가 데이터 폴링 시작
        this.startPolling();
    },

    beforeUnmount() {
        // 이벤트 리스너 정리
        document.removeEventListener('mouseup', this.onDocumentMouseUp);
        document.removeEventListener('mousemove', this.onDocumentMouseMove);

        // 폴링 정리
        this.stopPolling();
    },
    methods: {
        async loadBondGroups() {
            try {
                console.log('채권 그룹 로드 시작');
                const groupsBySector = {};

                // 4개 채권 유형에 대해 모두 조회
                for (const creditType of this.creditTypes) {
                    const groups = await bondService.getBondGroups(creditType);
                    groupsBySector[creditType] = groups;
                    console.log(`${creditType} 그룹 ${groups.length}개 로드됨`);
                }

                // 섹터별로 정렬하고 기타 그룹 추가
                this.bondGroups = this.organizeBondGroupsBySector(groupsBySector);
                console.log(`총 채권 그룹 ${this.bondGroups.length}개 구성 완료`);
            } catch (error) {
                console.error('채권 그룹 조회 실패:', error);
                throw error;
            }
        },

        organizeBondGroupsBySector(groupsBySector) {
            const organizedGroups = [];
            const sectorOrder = ['PUBLIC', 'BANK', 'CREDIT_FINANCE', 'COMPANY'];
            const sectorNames = {
                'PUBLIC': '공사채',
                'BANK': '은행채',
                'CREDIT_FINANCE': '여전채',
                'COMPANY': '회사채'
            };

            sectorOrder.forEach(creditType => {
                const groups = groupsBySector[creditType] || [];

                // 일반 그룹들 추가
                groups.forEach(group => {
                    organizedGroups.push({
                        ...group,
                        sector: creditType,
                        sectorName: sectorNames[creditType],
                        isOtherGroup: false
                    });
                });

                // 기타 그룹 추가
                organizedGroups.push({
                    id: `other_${creditType}`,
                    name: `기타 ${sectorNames[creditType]}`,
                    creditType: creditType,
                    sector: creditType,
                    sectorName: sectorNames[creditType],
                    isOtherGroup: true,
                    issuers: [] // 기타 그룹은 빈 발행사 목록
                });
            });

            return organizedGroups;
        },

        async loadSyncRecord() {
            try {
                this.syncRecord = await bondService.getDailySyncRecord(this.selectedDate);
            } catch (error) {
                console.error('동기화 기록 조회 실패:', error);
            }
        },

        async loadQuotes() {
            try {
                this.quotes = await bondService.getCreditQuotes(this.selectedDate);
                this.buildCellQuoteMap();
            } catch (error) {
                console.error('호가 정보 조회 실패:', error);
            }
        },

        async onDateChange() {
            // 날짜가 변경되면 선택된 셀들을 초기화하고 데이터를 다시 로드
            this.selectedCells = [];
            this.inquiryMessage = '';

            await this.refreshData(true); // 로딩 표시와 함께 새로고침

            // 날짜 변경 시 폴링 재시작
            this.restartPolling();
        },

        async refreshData(showLoading = false) {
            // 데이터만 새로고침 (선택된 셀과 문의내용 유지)
            // 채권 그룹은 변경되지 않으므로 호가와 동기화 기록만 새로고침
            if (showLoading) {
                this.loading = true;
            }

            try {
                await Promise.all([
                    this.loadSyncRecord(),
                    this.loadQuotes()
                ]);
            } catch (error) {
                console.error('데이터 새로고침 실패:', error);
            } finally {
                if (showLoading) {
                    this.loading = false;
                }
            }
        },

        startPolling() {
            // 기존 폴링이 있다면 정리
            this.stopPolling();

            // 30초(30000ms)마다 호가 데이터 새로고침
            this.pollingInterval = setInterval(async () => {
                await this.refreshData(false); // 로딩 표시 없이, 선택된 셀 유지하며 새로고침
            }, 30000);

        },

        stopPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }
        },

        restartPolling() {
            this.stopPolling();
            this.startPolling();
        },

        buildCellQuoteMap() {
            this.cellQuoteMap.clear();
            this.quotes.forEach((quote) => {
                const maturityRange = this.getMaturityRange(quote.maturityDate);
                let groupId = this.findGroupForQuote(quote);
                if (maturityRange && groupId) {
                    const key = `${groupId}-${maturityRange}`;
                    if (!this.cellQuoteMap.has(key)) {
                        this.cellQuoteMap.set(key, []);
                    }
                    this.cellQuoteMap.get(key).push(quote);
                }
            });
            // 기타 그룹별 호가 수 요약
            const otherGroupCounts = {};
            this.cellQuoteMap.forEach((quotes, key) => {
                if (key.startsWith('other_')) {
                    const groupType = key.split('-')[0];
                    otherGroupCounts[groupType] = (otherGroupCounts[groupType] || 0) + quotes.length;
                }
            });
        },

        findGroupForQuote(quote) {
            // 4개 섹터별로 순차적으로 매칭 시도
            const creditTypes = ['PUBLIC', 'BANK', 'CREDIT_FINANCE', 'COMPANY'];

            for (const creditType of creditTypes) {
                // 1. 해당 섹터의 일반 그룹에서 발행사 매칭 시도
                const matchedGroup = this.bondGroups.find(group =>
                    !group.isOtherGroup &&
                    group.creditType === creditType &&
                    group.issuers &&
                    group.issuers.some(issuer => issuer.id === quote.bondIssuerId)
                );

                if (matchedGroup) {
                    return matchedGroup.id;
                }
            }

            // 2. 모든 섹터에서 매칭되지 않았다면, 호가의 creditType으로 기타 그룹 결정
            const otherGroup = this.bondGroups.find(group =>
                group.isOtherGroup && group.creditType === quote.creditType
            );
            return otherGroup ? otherGroup.id : null;
        },

        getMaturityRange(maturityDate) {
            const today = new Date();
            const maturity = new Date(maturityDate);
            const diffTime = maturity - today;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            const diffMonths = diffDays / 30.44; // 평균 월 일수
            const diffYears = diffMonths / 12;

            if (diffMonths <= 3) return '3M';
            if (diffMonths <= 6) return '6M';
            if (diffMonths <= 9) return '9M';
            if (diffYears <= 1) return '1Y';
            if (diffYears <= 1.5) return '1.5Y';
            if (diffYears <= 2) return '2Y';
            if (diffYears <= 2.5) return '2.5Y';
            if (diffYears <= 3) return '3Y';
            if (diffYears <= 4) return '4Y';
            if (diffYears <= 5) return '5Y';
            if (diffYears <= 6) return '6Y';
            if (diffYears <= 7) return '7Y';
            if (diffYears <= 10) return '10Y';

            return null; // 10년 초과는 표시하지 않음
        },


        findIssuerById(bondIssuerId) {
            // 일반 그룹(기타 그룹 제외)의 발행사 목록에서 해당 발행사 찾기
            for (const group of this.bondGroups) {
                if (!group.isOtherGroup && group.issuers) {
                    const issuer = group.issuers.find(issuer => issuer.id === bondIssuerId);
                    if (issuer) {
                        return issuer;
                    }
                }
            }
            return null;
        },

        isFirstGroupInSector(group, index) {
            if (index === 0) return true; // 첫 번째 그룹은 항상 섹터 헤더 표시

            const previousGroup = this.bondGroups[index - 1];
            return group.sector !== previousGroup.sector;
        },

        hasQuoteInCell(groupId, maturity) {
            const key = `${groupId}-${maturity}`;
            return this.cellQuoteMap.has(key) && this.cellQuoteMap.get(key).length > 0;
        },

        isSelectedCell(groupId, maturity) {
            const key = `${groupId}-${maturity}`;
            return this.selectedCells.some(cell => cell.key === key);
        },

        onCellMouseDown(group, maturity, event) {
            // 우클릭은 무시
            if (event.button !== 0) return;

            event.preventDefault();

            this.mouseDownTime = Date.now();
            this.mouseDownPosition = { x: event.clientX, y: event.clientY };

            const key = `${group.id}-${maturity}`;
            const isCurrentlySelected = this.isSelectedCell(group.id, maturity);

            this.dragStartCell = { group, maturity, key };
            this.dragMode = isCurrentlySelected ? 'deselect' : 'select';
            this.dragSelection.clear();

            // 시작 셀을 드래그 선택에 추가
            this.dragSelection.add(key);

            // 전역 마우스 이벤트 리스너 추가
            document.addEventListener('mouseup', this.onDocumentMouseUp);
            document.addEventListener('mousemove', this.onDocumentMouseMove);
        },

        onCellMouseEnter(group, maturity) {
            if (!this.isDragging) return;

            const key = `${group.id}-${maturity}`;
            this.dragSelection.add(key);
        },

        onCellMouseUp() {
            // finalizeDragSelection에서 처리
        },

        onTableMouseLeave() {
            // 테이블을 벗어나면 드래그 종료
            if (this.isDragging) {
                this.finalizeDragSelection();
            }
        },

        onTableMouseUp() {
            if (this.isDragging) {
                this.finalizeDragSelection();
            }
        },

        onDocumentMouseUp() {
            if (this.isDragging || this.dragStartCell) {
                this.finalizeDragSelection();
            }
        },

        onDocumentMouseMove(event) {
            // 마우스가 움직이기 시작하면 드래그 모드 활성화
            if (!this.isDragging && this.dragStartCell) {
                const moveDistance = Math.sqrt(
                    Math.pow(event.clientX - this.mouseDownPosition.x, 2) +
                    Math.pow(event.clientY - this.mouseDownPosition.y, 2)
                );

                // 5픽셀 이상 움직이면 드래그로 판단
                if (moveDistance > 5) {
                    this.isDragging = true;
                }
            }

            if (!this.isDragging) return;

            // 마우스 위치에 있는 셀 찾기
            const element = document.elementFromPoint(event.clientX, event.clientY);
            if (element && element.classList.contains('data-cell')) {
                // 셀의 데이터 속성에서 그룹과 만기 정보 추출 (필요시 구현)
            }
        },

        finalizeDragSelection() {
            const clickTime = Date.now() - this.mouseDownTime;
            const isClick = !this.isDragging && clickTime < 200; // 200ms 이하이고 드래그하지 않았으면 클릭

            if (isClick && this.dragStartCell) {
                // 단일 클릭 처리
                const { group, maturity, key } = this.dragStartCell;
                const existingIndex = this.selectedCells.findIndex(cell => cell.key === key);

                if (existingIndex >= 0) {
                    // 이미 선택된 셀이면 제거
                    this.selectedCells.splice(existingIndex, 1);
                } else {
                    // 새로 선택하는 셀이면 추가
                    this.selectedCells.push({
                        key,
                        groupId: group.id,
                        groupName: group.name,
                        maturity
                    });
                }
            } else if (this.isDragging) {
                // 드래그 선택 처리
                this.dragSelection.forEach(key => {
                    const existingIndex = this.selectedCells.findIndex(cell => cell.key === key);

                    if (this.dragMode === 'select' && existingIndex === -1) {
                        // 선택 모드: 선택되지 않은 셀들을 선택
                        const [groupId, maturity] = key.split('-');
                        const group = this.bondGroups.find(g => g.id.toString() === groupId);
                        if (group) {
                            this.selectedCells.push({
                                key,
                                groupId: parseInt(groupId),
                                groupName: group.name,
                                maturity
                            });
                        }
                    } else if (this.dragMode === 'deselect' && existingIndex >= 0) {
                        // 해제 모드: 선택된 셀들을 해제
                        this.selectedCells.splice(existingIndex, 1);
                    }
                });
            }

            // 드래그 상태 초기화
            this.isDragging = false;
            this.dragStartCell = null;
            this.dragMode = null;
            this.dragSelection.clear();
            this.mouseDownTime = 0;
            this.mouseDownPosition = null;

            // 전역 이벤트 리스너 제거
            document.removeEventListener('mouseup', this.onDocumentMouseUp);
            document.removeEventListener('mousemove', this.onDocumentMouseMove);
        },

        isDragSelecting(groupId, maturity) {
            const key = `${groupId}-${maturity}`;
            return this.isDragging && this.dragSelection.has(key);
        },

        preventSelection(event) {
            event.preventDefault();
        },

        removeSelectedCell(key) {
            const index = this.selectedCells.findIndex(cell => cell.key === key);
            if (index >= 0) {
                this.selectedCells.splice(index, 1);
            }
        },

        getTelegramLink(phone) {
            // 전화번호에서 하이픈 제거하고 텔레그램 링크 생성
            const cleanPhone = phone.replace(/-/g, '');

            // 선택된 셀 정보와 문의내용을 텍스트로 구성
            let messageText = '';

            if (this.selectedCells.length > 0) {
                messageText += '선택된 셀:\n';
                this.selectedCells.forEach(cell => {
                    messageText += `- ${cell.groupName} ${cell.maturity}\n`;
                });
                messageText += '\n';
            }

            if (this.inquiryMessage.trim()) {
                messageText += `문의내용:\n${this.inquiryMessage.trim()}`;
            }

            if (!messageText) {
                messageText = '안녕하세요. 문의드립니다.';
            }

            // URL 인코딩
            const encodedText = encodeURIComponent(messageText);

            return `https://t.me/+82${cleanPhone.substring(1)}?text=${encodedText}`;
        },

        formatSyncTime(dateTimeString) {
            if (!dateTimeString) return '-';

            const date = new Date(dateTimeString);
            return date.toLocaleString('ko-KR', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            });
        },

        async copyToClipboard() {
            if (this.selectedCells.length === 0 && !this.inquiryMessage.trim()) {
                alert('선택된 셀이나 문의 내용이 없습니다.');
                return;
            }

            let copyText = '';

            if (this.selectedCells.length > 0) {
                copyText += '선택된 셀:\n';
                this.selectedCells.forEach(cell => {
                    copyText += `- ${cell.groupName} - ${cell.maturity}\n`;
                });
                copyText += '\n';
            }

            if (this.inquiryMessage.trim()) {
                copyText += '문의 내용:\n';
                copyText += this.inquiryMessage.trim();
            }

            try {
                await navigator.clipboard.writeText(copyText);
                alert('클립보드에 복사되었습니다.');
            } catch (error) {
                console.error('클립보드 복사 실패:', error);
                alert('클립보드 복사에 실패했습니다.');
            }
        }
    }
};
</script>

<style scoped></style>